import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

const DEFAULT_TOP = 64;
const STORAGE_KEY = "denker.toastTop";

function readTop(): number {
  if (typeof window === "undefined") return DEFAULT_TOP;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : DEFAULT_TOP;
  } catch {
    return DEFAULT_TOP;
  }
}

function clampTop(next: number): number {
  if (typeof window === "undefined") return next;
  return Math.min(Math.max(next, 8), Math.max(8, window.innerHeight - 120));
}

/**
 * Vertical drag for the whole right-edge toast column (stack entries + rail
 * section move together, so the surfaces can never overlap). Drag with a
 * >3px threshold so hover-reveal still works; the offset persists per machine.
 */
export function useDraggableTop(): {
  top: number;
  gripHandlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: () => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
  /** True once if the pointer moved past the drag threshold since the last call. */
  takeDragged: () => boolean;
} {
  const [top, setTop] = useState<number>(() => readTop());
  const topRef = useRef(top);
  topRef.current = top;
  const dragged = useRef(false);

  return {
    top,
    gripHandlers: {
      // No setPointerCapture here: capturing retargets the follow-up click
      // to this wrapper, which would swallow the pill's own buttons. Window
      // listeners track the drag and leave click targeting untouched; a real
      // drag still suppresses the click via takeDragged below.
      onPointerDown: (event) => {
        // Text inputs, menus, and links handle themselves; everything else
        // (including pill buttons) may start a drag — a clean click without
        // movement still fires normally, suppressed only after a real drag.
        if (
          (event.target as HTMLElement | null)?.closest?.(
            "input, textarea, select, a, [contenteditable='true'], [role='menu'], [role='menuitem']",
          )
        ) {
          return;
        }
        const startY = event.clientY;
        const startTop = topRef.current;
        let moved = false;
        let next = startTop;
        const onMove = (moveEvent: PointerEvent) => {
          next = clampTop(startTop + (moveEvent.clientY - startY));
          if (Math.abs(next - startTop) > 3) moved = true;
          if (moved) {
            dragged.current = true;
            setTop(next);
          }
        };
        const onUp = () => {
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          window.removeEventListener("pointercancel", onUp);
          if (moved) {
            try {
              window.localStorage.setItem(STORAGE_KEY, String(next));
            } catch {
              // Storage unavailable — the offset still holds for this session.
            }
          }
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
      },
      onPointerMove: () => undefined,
      onPointerUp: () => undefined,
      onPointerCancel: () => undefined,
    },
    takeDragged: () => {
      const was = dragged.current;
      dragged.current = false;
      return was;
    },
  };
}
